import { createSlice } from '@reduxjs/toolkit';
import { cloneDeep, some } from 'lodash';

import SearchDropdown from 'src/models/SearchDropdown/SearchDropdown';
import generateId from 'src/lib/generateId';
import { AppThunk } from 'src/actions/shared/sharedTypes';
import { DomainError as StandardDomainError, Unauthorized } from '@ad-scripts/application/lib/error';
import { logout } from 'src/actions/auth/authSlice';
import { registerGatewayObserverCreator } from 'src/configuration/registerGatewayObserverCreator';
import { translateErrors } from 'src/adapters/validateType';

type AnswerSearchQuery = {
  fieldName: string;
  language: string;
  text: string;
  filter?: AnswerSearchQueryFilter;
};

export type AnswerSearchQueryFilter = {
  audienceIds?: string[];
  hasFields?: Record<string, string>[];
};

function initSearchDropdownResource() {
  const name = 'searchDropdown';
  const initialState: SearchDropdown | {} = {};

  const slice = createSlice({
    name,
    initialState,
    reducers: {
      initSearchField(state, { payload: { fieldName } }) {
        state[fieldName] = {
          value: [],
          loadingState: 'unstarted',
          persistingState: 'unstarted',
          errors: [],
        };
      },
      fetchStarted(state, { payload: { fieldName } }) {
        if (state[fieldName]) {
          state[fieldName].loadingState = 'loading';
          state[fieldName].errors = [];
        }
      },
      fetchSuccess(state, { payload: { fieldName, data = {} } }) {
        if (state[fieldName]) {
          state[fieldName].loadingState = 'succeeded';
          state[fieldName].value = data.hits ?? [];
        }
      },
      addAnswer(state, { payload: { fieldName, answer } }) {
        if (state[fieldName]) {
          const answerExists = some(state[fieldName].value, item => item.id === answer.id);
          if (answerExists) {
            return;
          }

          state[fieldName].value = cloneDeep([...state[fieldName].value, answer]);
        }
      },
      fetchRequestError(state, { payload: { fieldName } }) {
        if (state[fieldName]) {
          state[fieldName].loadingState = 'failed';
        }
      },
      fetchError(state, { payload: { fieldName, error } }) {
        if (state[fieldName]) {
          state[fieldName].loadingState = 'failed';
          state[fieldName].errors = error;
        }
      },
      updateRequestError(state, { payload: { fieldName } }) {
        if (state[fieldName]) {
          state[fieldName].persistingState = 'failed';
        }
      },

      broadcast(state, { payload: { fieldName, item } }) {
        if (state[fieldName]) {
          state[fieldName].value = item;
        }
      },

      removeField(state, { payload: { fieldName } }) {
        delete state[fieldName];
      },
      reset() {
        return initialState;
      },
    },
  });

  const { fetchStarted } = slice.actions;

  const request: AppThunk =
    ({ fieldName, language, text, filter }: AnswerSearchQuery) =>
    (dispatch, getState, { gateways }) => {
      const id = generateId();
      const languages = language ? [language] : [];
      const params = { query: { languages, text, filter } };

      dispatch(fetchStarted({ fieldName }));
      // this gateway is configured with an observer rather returning a promise
      gateways[name].request(fieldName, id, params);
    };

  const observerCreator = ({ actions }) => {
    const { fetchError, fetchRequestError, fetchSuccess } = actions;

    return ({ dispatch }) => ({
      onFetchSuccess(response) {
        dispatch(fetchSuccess(response));
      },
      onFetchError(fieldName, error) {
        if (error instanceof StandardDomainError) {
          return dispatch(fetchError({ fieldName, error: translateErrors(error.errors) }));
        }

        if (error instanceof Unauthorized) {
          return dispatch(logout());
        }

        dispatch(fetchRequestError({ fieldName, error }));
      },
    });
  };
  const createObserver = observerCreator({ actions: slice.actions });
  registerGatewayObserverCreator(name, createObserver);

  const sliceWithEffects = {
    ...slice,
    // Effects cause API requests (mostly). They can (and do) call actions to also change redux store
    effects: {
      request,
    },
  };

  return sliceWithEffects;
}

export default initSearchDropdownResource();
